// ────────────────────────────────────────────────────────────────────────
// كشف مادة (Material Statement)
// A per-product statement-style view, mirroring the customer statement:
// pick a material → show its image + name + brand → filter by date range
// → render every stock event (purchase / sale-customer / sale-admin /
// sale-supervisor / damage / manual-edit) as a chronological ledger with
// running balance. Includes WhatsApp, PDF and Print export.
// ────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Search, Package, Loader2, Printer, Share2, FileText, X,
  ArrowUpRight, ArrowDownLeft, Truck, ShoppingCart, User as UserIcon,
  UserCog, AlertTriangle, Pencil, Calendar, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { smartMatch } from "../../utils/arabicSearch";
import { printHTML } from "../../utils/printHTML";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
// Product images live in object storage. `image_path` is a bucket-relative
// key like "jumlaty/products/xyz.jpeg" — never a full URL — so we always
// serve them through the backend `/files` proxy.
const imgSrc = (p) => {
  if (!p) return "/placeholder.png";
  if (p.image_url && /^https?:\/\//.test(p.image_url)) return p.image_url;
  const key = p.image_path || p.image_url || p.image;
  return key ? `${API}/files/${key}` : "/placeholder.png";
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
const splitDate = (iso) => {
  if (!iso) return { day: "—", time: "" };
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString("en-GB", { year: "2-digit", month: "2-digit", day: "2-digit" }),
      time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch { return { day: iso, time: "" }; }
};

// ─── Product search + picker ─────────────────────────────────────────
const ProductPicker = ({ selected, onSelect, onClear, refreshTick = 0 }) => {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Cache-buster so newly-added products always show up.
        const { data } = await axios.get(`${API}/admin/products`, {
          params: { _t: Date.now() },
          headers: { "Cache-Control": "no-cache" },
        });
        setList(Array.isArray(data) ? data : []);
      } catch {
        toast.error("فشل جلب المواد");
      } finally { setLoading(false); }
    })();
  }, [refreshTick]);

  const filtered = useMemo(() => {
    // If admin isn't typing anything, show ALL products (was capped at
    // 60 before — that hid newly-added products silently once the list
    // grew past 60). When typing we still filter but keep every match.
    if (!q.trim()) return list;
    return list.filter((p) => {
      const hay = `${p.name_ar || ""} ${p.name || ""} ${p.brand || ""}`.trim();
      return smartMatch(hay, q);
    });
  }, [q, list]);

  if (selected) {
    return (
      <div className="rounded-2xl bg-white border-2 border-blue-200 p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <img
            src={imgSrc(selected)}
            alt=""
            onError={(e) => { e.currentTarget.src = "/placeholder.png"; }}
            className="w-16 h-16 rounded-xl object-cover bg-slate-100 border border-slate-200"
            data-testid="ml-selected-image"
          />
          <div className="flex-1 min-w-0">
            <p className="font-black text-slate-900 truncate" data-testid="ml-selected-name">
              {selected.name_ar || selected.name || "—"}
            </p>
            <p className="text-xs text-slate-500 truncate">
              {selected.brand || "بدون علامة"} · المخزون الآن: <b className="text-blue-700 tabular-nums" dir="ltr">{fmt(selected.stock)}</b> كرتون
            </p>
          </div>
          <button
            onClick={onClear}
            className="p-2 rounded-full bg-red-50 hover:bg-red-100 text-red-600 transition"
            title="اختر مادة ثانية"
            data-testid="ml-clear-selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border-2 border-slate-200 p-3 shadow-sm space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث عن اسم المادة أو العلامة التجارية…"
          className="w-full pr-9 pl-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm"
          data-testid="ml-product-search"
          autoFocus
        />
      </div>
      {loading ? (
        <div className="py-8 flex items-center justify-center text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-6">لا توجد نتائج</p>
      ) : (
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => onSelect(p)}
              className="w-full flex items-center gap-2 p-2 hover:bg-blue-50 rounded-lg transition text-right"
              data-testid={`ml-product-row-${p.id}`}
            >
              <img
                src={imgSrc(p)}
                alt=""
                onError={(e) => { e.currentTarget.src = "/placeholder.png"; }}
                className="w-10 h-10 rounded-lg object-cover bg-slate-100"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800 truncate">{p.name_ar || p.name}</p>
                <p className="text-[10px] text-slate-500 truncate">
                  {p.brand || "—"} · مخزون: <span className="text-blue-700 tabular-nums" dir="ltr">{fmt(p.stock)}</span>
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Event type meta (icon, colour, label) ──────────────────────────
const TYPE_META = {
  purchase:        { icon: Truck,        color: "#0ea5e9", label: "شراء",          sign: "+" },
  sale_customer:   { icon: ShoppingCart, color: "#059669", label: "بيع لزبون",     sign: "−" },
  sale_admin:      { icon: UserIcon,     color: "#f97316", label: "بيع مباشر",     sign: "−" },
  sale_supervisor: { icon: UserCog,      color: "#8b5cf6", label: "بيع من مشرف",   sign: "−" },
  damage:          { icon: AlertTriangle,color: "#dc2626", label: "تالف",          sign: "−" },
  adjustment:      { icon: Pencil,       color: "#6366f1", label: "تعديل يدوي",    sign: "±" },
};

// ─── Ledger row — ONE running-balance box only ────────────────────
// User request (Feb 2026): "لا تسوي مربعين مربع واحد بس، ينقص ويزود
// حسب الحركة". So we show a single prominent box on the LEFT with the
// running balance after the event; the +/- delta is rendered inline as
// coloured text inside the description block, not as its own box.
const EventRow = ({ ev }) => {
  const meta = TYPE_META[ev.type] || { icon: Package, color: "#64748b", label: "حركة", sign: "" };
  const Icon = meta.icon;
  const d = splitDate(ev.date);
  const deltaN = Number(ev.delta || 0);
  const isPositive = deltaN > 0;
  const isNegative = deltaN < 0;
  return (
    <div
      className="flex items-stretch gap-2 p-2.5 rounded-xl border border-slate-200 bg-white hover:shadow-md transition"
      data-testid={`ml-event-row-${ev.type}`}
    >
      {/* Icon + description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-1.5 mb-0.5">
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-black"
            style={{ background: `${meta.color}14`, color: meta.color }}
          >
            <Icon className="w-3 h-3" />
            {meta.label}
          </span>
          {/* Inline delta — coloured text, no separate box */}
          <span
            className={`text-xs font-black tabular-nums ${
              isPositive ? "text-emerald-700" : isNegative ? "text-red-700" : "text-slate-500"
            }`}
            dir="ltr"
          >
            {isPositive ? "+" : ""}{deltaN.toLocaleString("en-US")}
          </span>
          <span className="text-[10px] text-slate-400 tabular-nums" dir="ltr">
            · {d.day} · {d.time}
          </span>
          {ev.reference ? (
            <span className="text-[10px] text-slate-400 tabular-nums" dir="ltr">
              · #{ev.reference}
            </span>
          ) : null}
        </div>
        <p className="text-xs text-slate-800 leading-tight">{ev.description}</p>
        {ev.actor ? <p className="text-[10px] text-slate-400 mt-0.5">بواسطة: {ev.actor}</p> : null}
      </div>

      {/* Single running-balance box */}
      <div
        className="flex-shrink-0 flex flex-col items-center justify-center rounded-xl px-3 min-w-[70px]"
        style={{ background: "#eff6ff", border: "1.5px solid #bfdbfe" }}
        title="الرصيد بعد هذه الحركة"
      >
        <span className="text-[9px] text-blue-600">الرصيد</span>
        <span className="text-lg font-black text-blue-800 tabular-nums leading-tight" dir="ltr">
          {fmt(ev.balance_after)}
        </span>
      </div>
    </div>
  );
};

// ─── Summary strip ──────────────────────────────────────────────────
const KPI = ({ label, value, unit = "كرتون", color = "#0f172a", icon: Icon, testId }) => (
  <div
    className="rounded-2xl bg-white border-2 p-2.5 shadow-sm text-center flex-1 min-w-[110px]"
    style={{ borderColor: `${color}25` }}
    data-testid={testId}
  >
    <div className="flex items-center justify-center gap-1 mb-0.5">
      {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
      <p className="text-[10px] font-black" style={{ color }}>{label}</p>
    </div>
    <p className="text-lg font-black tabular-nums" style={{ color }} dir="ltr">
      {typeof value === "number" && value < 0 ? "" : ""}{fmt(value)}
    </p>
    <p className="text-[9px] text-slate-500">{unit}</p>
  </div>
);

// ─── Main tab ────────────────────────────────────────────────────────
const MaterialLedgerTab = ({ refreshTick = 0 }) => {
  const [product, setProduct] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all"); // all|in|out|adjustment
  const [reloadTick, setReloadTick] = useState(0);
  const [resetting, setResetting] = useState(false);

  const handleResetLedger = async () => {
    const ok = window.confirm(
      "هل أنت متأكد؟\n\nراح تصفّر عرض «كشف مادة» لكل المواد — يعني بس الحركات المعروضة على الشاشة.\n\n" +
      "المخزن الفعلي راح يبقى كما هو تماماً — لا تعديل ولا مساس.\n\n" +
      "بعد التصفير، أي شراء أو بيع أو تعديل جديد راح يبدأ سطر جديد بالكشف."
    );
    if (!ok) return;
    try {
      setResetting(true);
      await axios.post(`${API}/admin/material-ledger/reset-all`);
      toast.success("تم تصفير كشف المادة. المخزن الفعلي لم يتأثر.");
      setReloadTick((t) => t + 1);
    } catch {
      toast.error("فشل التصفير");
    } finally { setResetting(false); }
  };

  // Fetch ledger whenever selection, dates or reset tick changes.
  useEffect(() => {
    if (!product?.id) { setData(null); return; }
    (async () => {
      try {
        setLoading(true);
        const params = { product_id: product.id };
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const { data } = await axios.get(`${API}/admin/material-movement`, { params });
        setData(data || null);
      } catch {
        toast.error("فشل تحميل كشف المادة");
      } finally { setLoading(false); }
    })();
  }, [product?.id, dateFrom, dateTo, reloadTick]);

  const events = data?.events || [];
  const filteredEvents = useMemo(() => {
    if (filterType === "all") return events;
    if (filterType === "in") return events.filter((e) => Number(e.delta) > 0);
    if (filterType === "out") return events.filter((e) => Number(e.delta) < 0);
    if (filterType === "adjustment") return events.filter((e) => e.type === "adjustment");
    return events;
  }, [events, filterType]);

  // ─── Export helpers ────────────────────────────────────────────
  const buildPrintHtml = () => {
    if (!data) return "";
    const rows = filteredEvents.map((e) => {
      const meta = TYPE_META[e.type] || {};
      const isPos = Number(e.delta) > 0;
      const isNeg = Number(e.delta) < 0;
      const color = isPos ? "#059669" : isNeg ? "#dc2626" : "#64748b";
      return `
        <tr>
          <td style="padding:6px;border:1px solid #e2e8f0;font-size:11px;white-space:nowrap;">${fmtDate(e.date)}</td>
          <td style="padding:6px;border:1px solid #e2e8f0;font-size:11px;color:${meta.color || "#0f172a"};font-weight:800;">${meta.label || ""}</td>
          <td style="padding:6px;border:1px solid #e2e8f0;font-size:11px;">${e.description || ""}</td>
          <td style="padding:6px;border:1px solid #e2e8f0;font-size:11px;text-align:center;color:${color};font-weight:900;" dir="ltr">${isPos ? "+" : ""}${Number(e.delta).toLocaleString("en-US")}</td>
          <td style="padding:6px;border:1px solid #e2e8f0;font-size:11px;text-align:center;font-weight:900;color:#1e40af;" dir="ltr">${fmt(e.balance_after)}</td>
        </tr>`;
    }).join("");
    const s = data.summary || {};
    return `<!DOCTYPE html>
<html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>كشف مادة — ${product?.name_ar || ""}</title>
<style>
  body{font-family:'Tajawal','Tahoma',sans-serif;margin:16px;color:#0f172a;background:#fff;}
  h1{font-size:18px;margin:0 0 4px;}
  .subtle{color:#64748b;font-size:11px;margin-bottom:12px;}
  .kpi-row{display:flex;gap:6px;margin:8px 0 12px;flex-wrap:wrap;}
  .kpi{flex:1;min-width:120px;border:1px solid #e2e8f0;border-radius:8px;padding:6px 8px;text-align:center;}
  .kpi small{display:block;color:#64748b;font-size:10px;}
  .kpi b{display:block;font-size:15px;color:#0f172a;}
  table{width:100%;border-collapse:collapse;margin-top:6px;}
  th{background:#f1f5f9;padding:6px;font-size:11px;border:1px solid #e2e8f0;}
</style></head><body>
<h1>كشف مادة — ${product?.name_ar || product?.name || ""}</h1>
<p class="subtle">
  ${product?.brand ? `العلامة: ${product.brand} · ` : ""}
  ${dateFrom || dateTo ? `الفترة: ${dateFrom || "البداية"} → ${dateTo || "الآن"}` : "كل الفترات"}
  · طُبع: ${new Date().toLocaleString("en-GB")}
</p>
<div class="kpi-row">
  <div class="kpi"><small>الرصيد الابتدائي</small><b>${fmt(s.opening_balance)}</b></div>
  <div class="kpi"><small>مُشترى</small><b style="color:#0ea5e9;">${fmt(s.total_purchased)}</b></div>
  <div class="kpi"><small>مُباع</small><b style="color:#059669;">${fmt(s.total_sold)}</b></div>
  <div class="kpi"><small>تالف</small><b style="color:#dc2626;">${fmt(s.total_damaged)}</b></div>
  <div class="kpi"><small>تعديلات يدوية</small><b>${(Number(s.adjustments_delta||0)>=0?"+":"")}${fmt(s.adjustments_delta)}</b></div>
  <div class="kpi"><small>المخزون الآن</small><b style="color:#1e40af;">${fmt(s.current_stock)}</b></div>
</div>
<table>
  <thead>
    <tr><th>التاريخ</th><th>النوع</th><th>الوصف</th><th>الحركة</th><th>الرصيد بعد</th></tr>
  </thead>
  <tbody>${rows || `<tr><td colspan="5" style="padding:10px;text-align:center;color:#94a3b8;">لا توجد حركات</td></tr>`}</tbody>
</table>
</body></html>`;
  };

  const handlePrint = () => {
    const html = buildPrintHtml();
    if (!html) return;
    printHTML(html);
  };

  const handleWhatsApp = () => {
    if (!data) return;
    const s = data.summary || {};
    const lines = [
      `📋 كشف مادة — ${product?.name_ar || product?.name || ""}`,
      product?.brand ? `العلامة: ${product.brand}` : "",
      dateFrom || dateTo ? `الفترة: ${dateFrom || "البداية"} → ${dateTo || "الآن"}` : "الفترة: كل الحركات",
      "",
      `• رصيد ابتدائي: ${fmt(s.opening_balance)} كرتون`,
      `• مُشترى: +${fmt(s.total_purchased)}`,
      `• مُباع: −${fmt(s.total_sold)}`,
      `• تالف: −${fmt(s.total_damaged)}`,
      `• تعديلات: ${Number(s.adjustments_delta||0)>=0?"+":""}${fmt(s.adjustments_delta)}`,
      `• المخزون الآن: ${fmt(s.current_stock)} كرتون`,
      "",
      "— جُملَتي —",
    ].filter(Boolean).join("\n");
    window.open(`https://wa.me/?text=${encodeURIComponent(lines)}`, "_blank");
  };

  const handlePdf = handlePrint; // Same code path — user picks "حفظ PDF" in print dialog

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="material-ledger-tab">
      <div className="flex items-center gap-2">
        <div className="p-2 rounded-xl bg-blue-100">
          <Package className="w-5 h-5 text-blue-700" />
        </div>
        <div className="flex-1">
          <h2 className="font-black text-lg text-slate-900">كشف مادة</h2>
          <p className="text-xs text-slate-500">جدول زمني لكل حركة مخزون على المادة، مع رصيد جاري</p>
        </div>
        <button
          onClick={handleResetLedger}
          disabled={resetting}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-black transition disabled:opacity-50"
          title="يمسح كل الحركات السابقة من العرض. المخزن الفعلي لا يتأثر."
          data-testid="ml-reset-ledger"
        >
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
          تصفير الكشف
        </button>
      </div>

      <ProductPicker
        selected={product}
        onSelect={(p) => { setProduct(p); setDateFrom(""); setDateTo(""); }}
        onClear={() => { setProduct(null); setData(null); }}
        refreshTick={refreshTick}
      />

      {product && (
        <>
          {/* Date range filter */}
          <div className="rounded-2xl bg-white border-2 border-slate-200 p-3 shadow-sm">
            <p className="text-xs font-black text-slate-700 flex items-center gap-1.5 mb-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              فترة العرض
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500">من</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="ml-date-from"
                  className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">إلى</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  data-testid="ml-date-to"
                  className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                className="mt-2 text-[11px] text-slate-500 hover:text-slate-700 underline"
                data-testid="ml-clear-dates"
              >
                عرض كل الحركات (بدون فلتر)
              </button>
            )}
          </div>

          {loading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : !data ? (
            <p className="text-center text-slate-400 py-8">لا توجد بيانات</p>
          ) : (
            <>
              {/* KPI strip */}
              <div className="flex gap-2 flex-wrap">
                <KPI label="رصيد ابتدائي" value={data.summary?.opening_balance || 0} color="#64748b" icon={Package} testId="ml-kpi-open" />
                <KPI label="مُشترى" value={data.summary?.total_purchased || 0} color="#0ea5e9" icon={Truck} testId="ml-kpi-in" />
                <KPI label="مُباع" value={data.summary?.total_sold || 0} color="#059669" icon={ShoppingCart} testId="ml-kpi-out" />
                <KPI label="تالف" value={data.summary?.total_damaged || 0} color="#dc2626" icon={AlertTriangle} testId="ml-kpi-damage" />
                <KPI label="تعديلات" value={data.summary?.adjustments_delta || 0} color="#6366f1" icon={Pencil} testId="ml-kpi-adj" />
                <KPI label="المخزون الآن" value={data.summary?.current_stock || 0} color="#1e40af" icon={Package} testId="ml-kpi-current" />
              </div>

              {/* Export bar */}
              <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-2xl p-2">
                <button
                  onClick={handlePrint}
                  disabled={!filteredEvents.length}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  data-testid="ml-export-print"
                >
                  <Printer className="w-3.5 h-3.5" /> طباعة
                </button>
                <button
                  onClick={handlePdf}
                  disabled={!filteredEvents.length}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-bold hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  data-testid="ml-export-pdf"
                >
                  <FileText className="w-3.5 h-3.5" /> PDF
                </button>
                <button
                  onClick={handleWhatsApp}
                  disabled={!filteredEvents.length}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  data-testid="ml-export-whatsapp"
                >
                  <Share2 className="w-3.5 h-3.5" /> واتساب
                </button>

                {/* Type filter chips */}
                <div className="mr-auto flex flex-wrap items-center gap-1">
                  {[
                    { id: "all", label: "الكل" },
                    { id: "in", label: "دخول (+)" },
                    { id: "out", label: "خروج (−)" },
                    { id: "adjustment", label: "تعديلات" },
                  ].map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setFilterType(f.id)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition ${
                        filterType === f.id
                          ? "bg-slate-900 text-white"
                          : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                      }`}
                      data-testid={`ml-filter-${f.id}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ledger list */}
              <div className="space-y-2" data-testid="ml-ledger-list">
                {filteredEvents.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">لا توجد حركات في هذه الفترة</p>
                ) : filteredEvents.map((e, idx) => (
                  <EventRow key={`${e.date}-${idx}`} ev={e} />
                ))}
              </div>

              {/* Closing card — the balance at the very last event (i.e. current) */}
              <div
                className="rounded-2xl border-2 border-blue-300 bg-gradient-to-l from-blue-50 to-white p-3 shadow-sm flex items-center justify-between"
                data-testid="ml-closing-card"
              >
                <div>
                  <p className="text-[11px] text-blue-600 font-black">الرصيد النهائي — المخزون الآن</p>
                  <p className="text-[10px] text-slate-500">آخر رصيد فعلي بالمخزن بعد كل الحركات أعلاه</p>
                </div>
                <div className="text-left">
                  <p className="text-2xl font-black text-blue-800 tabular-nums" dir="ltr">
                    {fmt(data.summary?.current_stock || 0)}
                  </p>
                  <p className="text-[10px] text-slate-500 text-center">كرتون</p>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MaterialLedgerTab;
